import React from "react";
import { Button, Input, InputNumber, Select } from "antd";

export const fieldLabel = (key: string) => {
  const special: Record<string, string> = {
    dob: "Date of birth",
    ssn: "Social Security number",
    zipCode: "ZIP code",
    isDeceased: "Deceased",
    addressLine1: "Address line 1",
    addressLine2: "Address line 2",
  };
  const text =
    special[key] ||
    key.replace(/([a-z\d])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};
export const displayValue = (value: unknown) =>
  value === undefined || value === null || value === ""
    ? "Not provided"
    : typeof value === "boolean"
    ? value
      ? "Yes"
      : "No"
    : typeof value === "number"
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 10 }).format(
        value
      )
    : typeof value === "object"
    ? JSON.stringify(value, null, 2)
    : String(value);

export default function FieldInput({
  value,
  name,
  numeric,
  date,
  disabled,
  onChange,
}: {
  value: unknown;
  name: string;
  numeric?: boolean;
  date?: boolean;
  disabled?: boolean;
  onChange: (value: unknown) => void;
}) {
  if (Array.isArray(value))
    return (
      <div className="sd-nested">
        {value.map((item, index) => (
          <div key={index} className="sd-array-item">
            <FieldInput
              disabled={disabled}
              name={`${name} ${index + 1}`}
              value={item}
              onChange={(next) =>
                onChange(value.map((old, i) => (i === index ? next : old)))
              }
            />
            <Button
              disabled={disabled}
              type="text"
              danger
              aria-label={`Remove ${name} ${index + 1}`}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button
          disabled={disabled}
          size="small"
          onClick={() =>
            onChange([
              ...value,
              typeof value[0] === "object" && value[0]
                ? Object.fromEntries(
                    Object.keys(value[0]).map((key) => [key, ""])
                  )
                : "",
            ])
          }
        >
          Add item
        </Button>
      </div>
    );
  if (value && typeof value === "object")
    return (
      <div className="sd-nested">
        {Object.entries(value).map(([key, item]) => (
          <label key={key}>
            <span className="sd-field-label">{fieldLabel(key)}</span>
            <FieldInput
              disabled={disabled}
              name={`${name}: ${fieldLabel(key)}`}
              value={item}
              onChange={(next) => onChange({ ...value, [key]: next })}
            />
          </label>
        ))}
      </div>
    );
  if (typeof value === "boolean")
    return (
      <Select
        disabled={disabled}
        aria-label={name}
        value={value ? "yes" : "no"}
        options={[
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ]}
        onChange={(next) => onChange(next === "yes")}
      />
    );
  if (numeric || typeof value === "number")
    return (
      <InputNumber
        disabled={disabled}
        aria-label={name}
        style={{ width: "100%" }}
        value={typeof value === "number" ? value : undefined}
        onChange={onChange}
      />
    );
  return (
    <Input
      disabled={disabled}
      aria-label={name}
      type={date ? "date" : "text"}
      value={
        date && typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
          ? value.slice(0, 10)
          : String(value ?? "")
      }
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
